import { Box, Select, Typography } from '@mui/material';
import type { SelectProps } from '@mui/material/Select';
import type { SxProps, Theme } from '@mui/material/styles';
import { useRef } from 'react';
import {
  cockpitMenuPaperClassName,
  cockpitMenuPaperSx
} from './cockpitMenuStyles';
import {
  cockpitSelectContainerSx,
  cockpitSelectLabelSx,
  cockpitSelectRootSx
} from './cockpitSelectStyles';

type CockpitSelectProps = SelectProps<string> & {
  containerSx?: SxProps<Theme>;
  label: string;
  labelId?: string;
  testId?: string;
};

const mergeSx = (
  base: SxProps<Theme>,
  override: SxProps<Theme> | undefined
): SxProps<Theme> => [
  base,
  ...(Array.isArray(override) ? override : override ? [override] : [])
];

/** A labelled Cockpit selector with one compact, accessible visual contract. */
export function CockpitSelect({
  label,
  labelId: explicitLabelId,
  testId,
  id,
  MenuProps,
  containerSx,
  sx,
  className,
  ...selectProps
}: CockpitSelectProps) {
  const generatedId = useRef(
    `cockpit-select-${Math.random().toString(36).slice(2, 10)}`
  );
  const labelId = explicitLabelId || `${id || generatedId.current}-label`;

  return (
    <Box sx={mergeSx(cockpitSelectContainerSx, containerSx)}>
      <Typography
        id={labelId}
        component="span"
        sx={cockpitSelectLabelSx}
      >
        {label}
      </Typography>
      <Select
        {...selectProps}
        id={id}
        labelId={labelId}
        data-testid={testId}
        className={['cockpit-select', className].filter(Boolean).join(' ')}
        MenuProps={{
          ...MenuProps,
          PaperProps: {
            ...MenuProps?.PaperProps,
            className: [
              cockpitMenuPaperClassName,
              MenuProps?.PaperProps?.className
            ]
              .filter(Boolean)
              .join(' '),
            sx: mergeSx(cockpitMenuPaperSx, MenuProps?.PaperProps?.sx)
          },
          MenuListProps: {
            ...MenuProps?.MenuListProps,
            sx: mergeSx(
              { maxWidth: '100%', overflowX: 'hidden' },
              MenuProps?.MenuListProps?.sx
            )
          }
        }}
        sx={mergeSx(cockpitSelectRootSx, sx)}
      />
    </Box>
  );
}
